/**
 * tRPC 및 React Query 클라이언트를 설정하는 Provider입니다.
 *
 * 주요 기능:
 * - tRPC 클라이언트 초기화
 * - React Query QueryClient 설정
 * - WebSocket/HTTP 링크 설정
 *
 * @remarks
 * refetchOnWindowFocus를 비활성화하여 창 포커스 시 자동 재조회를 방지합니다.
 * 개발 환경에서는 ReactQueryDevtools가 표시됩니다.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { trpc, getEndingLink } from '../common/trpc';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useConnectionContext } from './connection-provider';

/**
 * tRPC 및 React Query Provider
 * @param children - 하위 컴포넌트
 */
export function TrpcProvider({ children }: { children: ReactNode }) {
  const { hostPort, apiUrl, token } = useConnectionContext();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 앱이 활성화(포커스) 받을 때 화면 그리기에 필요한 모든 데이터를 다시 받아오는 기능을 비활성화시킴. 너무 느림 10초 걸림.
            refetchOnWindowFocus: false, // default: true
            // placeholderData: keepPreviousData, // 이전 데이터 유지
          },
        },
      }),
  );
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [getEndingLink(hostPort, apiUrl, token)],
    }),
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </trpc.Provider>
  );
}
